import { useState, useEffect } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, Stack, Typography, Box, Chip, Sheet, Divider, Accordion, AccordionGroup, AccordionSummary, AccordionDetails, List, ListItem, Alert, LinearProgress, ModalOverflow } from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, XCircle, Question, CaretDown, Play } from "@phosphor-icons/react";

import { SafeHTML } from '../../utils';
import { APIGetTestResults } from '../../api';


export default function TestResultsModal({ open, test, token, onClose, lang: langProp }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Language: use prop from TestSessionPage or fallback to localStorage
    const lang = langProp || (() => { try { return localStorage.getItem('testLang') || 'en'; } catch { return 'en'; } })();
    const t = (en, ru) => lang === 'ru' && ru ? ru : en;

    useEffect(() => {
        if (open && test) {
            fetchTestResults();
        }
    }, [open, test]);

    const fetchTestResults = async () => {
        if (!test || !token) return;
        
        setLoading(true);
        setError('');
        try {
            const response = await APIGetTestResults(token, test.id);
            if (response.success) {
                setResults(response);
            } else {
                setError(response.error);
            }
        } catch (error) {
            console.error('Error fetching test results:', error);
            setError('Ошибка при загрузке результатов');
        } finally {
            setLoading(false);
        }
    };

    if (!test) return null;

    return (
        <AnimatePresence>
            {open && (
                <Modal 
                    open={open} 
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            sx={{
                                width: '90%',
                                maxWidth: 800,
                                maxHeight: '90vh',
                                overflow: 'auto'
                            }}
                        >
                            <ModalClose />
                            
                            <DialogTitle>
                                Результаты теста
                                <Typography level="body2" color="neutral">
                                    {test.title}
                                </Typography>
                            </DialogTitle>

                            <DialogContent>
                                {loading ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography level="body1" color="neutral">
                                            Загрузка результатов...
                                        </Typography>
                                    </Box>
                                ) : error ? (
                                    <Alert color="danger" variant="soft">
                                        {error}
                                    </Alert>
                                ) : results ? (
                                    <Stack spacing={3}>
                                        {/* Общая статистика */}
                                        <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'lg' }}>
                                            <Typography level="h4" sx={{ mb: 2 }}>
                                                Общая статистика
                                            </Typography>
                                            
                                            <Stack spacing={2}>
                                                {/* Прогресс бар */}
                                                <Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography level="body2">Точность выполнения</Typography>
                                                        <Typography level="body2" fontWeight="bold">
                                                            {results.test.accuracy_percentage}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        value={results.test.accuracy_percentage} 
                                                        determinate 
                                                        color={
                                                            results.test.accuracy_percentage >= 80 ? 'success' :
                                                            results.test.accuracy_percentage >= 60 ? 'warning' : 'danger'
                                                        }
                                                    />
                                                </Box>

                                                {/* Статистика в цифрах */}
                                                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                                    <StatCard 
                                                        label="Всего вопросов" 
                                                        value={results.test.total_questions}
                                                        color="neutral"
                                                    />
                                                    <StatCard 
                                                        label="Правильных" 
                                                        value={results.test.correct_answers}
                                                        color="success"
                                                    />
                                                    <StatCard 
                                                        label="Неправильных" 
                                                        value={results.test.incorrect_answers}
                                                        color="danger"
                                                    />
                                                    <StatCard 
                                                        label="Пропущенных" 
                                                        value={results.test.skipped_questions}
                                                        color="neutral"
                                                    />
                                                </Stack>

                                                {/* Дополнительная информация */}
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                    <Chip variant="soft" color="primary">
                                                        Режим: {results.test.mode === 'study' ? 'Учебный' : 'Тестовый'}
                                                    </Chip>
                                                    <Chip variant="outlined">
                                                        Завершен: {new Date(results.test.completed_date).toLocaleDateString('ru-RU')}
                                                    </Chip>
                                                </Stack>
                                            </Stack>
                                        </Sheet>

                                        <Divider />

                                        {/* Детальные результаты по вопросам */}
                                        <Box>
                                            <Typography level="h4" sx={{ mb: 2 }}>
                                                Детальные результаты
                                            </Typography>

                                            <AccordionGroup>
                                                {results.detailed_results.map((result, index) => (
                                                    <QuestionResult
                                                        key={result.question_id}
                                                        result={result}
                                                        index={index}
                                                        langFn={t}
                                                    />
                                                ))}
                                            </AccordionGroup>
                                        </Box>
                                    </Stack>
                                ) : null}
                            </DialogContent>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}

// Компонент для отображения статистической карточки
function StatCard({ label, value, color = 'neutral' }) {
    return (
        <Box sx={{ flex: 1, minWidth: 120 }}>
            <Sheet variant="soft" color={color} sx={{ p: 2, borderRadius: 'md', textAlign: 'center' }}>
                <Typography level="h3" color={color}>
                    {value}
                </Typography>
                <Typography level="body2">
                    {label}
                </Typography>
            </Sheet>
        </Box>
    );
}

// Компонент для отображения результата по одному вопросу
function QuestionResult({ result, index, langFn }) {
    const [expanded, setExpanded] = useState(false);
    const t = langFn || ((en) => en);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'correct':
                return <CheckCircle size={18} weight="fill" color="var(--joy-palette-success-500)" />;
            case 'incorrect':
                return <XCircle size={18} weight="fill" color="var(--joy-palette-danger-500)" />;
            case 'skipped':
                return <Question size={18} color="var(--joy-palette-neutral-500)" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'correct':
                return 'success';
            case 'incorrect':
                return 'danger';
            case 'skipped':
                return 'neutral';
            default:
                return 'neutral';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'correct':
                return 'Правильно';
            case 'incorrect':
                return 'Неправильно';
            case 'skipped':
                return 'Пропущено';
            default:
                return 'Не отвечено';
        }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={(event, isExpanded) => setExpanded(isExpanded)}
            sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'neutral.200',
                borderRadius: 'md',
                mb: 1
            }}
        >
            <AccordionSummary
                indicator={<CaretDown size={18} />}
                sx={{
                    backgroundColor: expanded ? 'neutral.50' : 'transparent',
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'neutral.200',
                    borderRadius: expanded ? 'md md 0 0' : 'md'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
                    {/* Номер вопроса и статус */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                        <Typography level="body2" color="neutral" sx={{ minWidth: 30 }}>
                            {index + 1}.
                        </Typography>
                        {getStatusIcon(result.status)}
                        <Chip 
                            size="sm" 
                            variant="soft" 
                            color={getStatusColor(result.status)}
                        >
                            {getStatusText(result.status)}
                        </Chip>
                    </Box>

                    {/* Текст вопроса */}
                    <Typography level="body1" sx={{ flex: 1 }}>
                        {t(result.question_text, result.question_text_ru)}
                    </Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Stack spacing={2}>
                    {/* Описание вопроса */}
                    {result.question_description && (
                        <Box>
                            <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                Описание вопроса:
                            </Typography>
                            <SafeHTML
                                html={t(result.question_description, result.question_description_ru)}
                                sx={{ 
                                    mt: 0,
                                    '& p': { mb: 1 },
                                    '& h1, & h2, & h3, & h4, & h5, & h6': { 
                                        mt: 0, mb: 1,
                                        color: 'text.primary'
                                    },
                                    '& blockquote': {
                                        borderLeft: '4px solid',
                                        borderColor: 'primary.300',
                                        pl: 2,
                                        ml: 0,
                                        fontStyle: 'italic',
                                        color: 'text.secondary'
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Ответ пользователя */}
                    <Box>
                        <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                            Ваш ответ:
                        </Typography>
                        {result.is_skipped ? (
                            <Alert color="neutral" variant="soft">
                                Вопрос был пропущен
                            </Alert>
                        ) : result.question_type === 'text' ? (
                            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                                <Typography level="body1">
                                    {result.user_text_answer || 'Ответ не предоставлен'}
                                </Typography>
                            </Sheet>
                        ) : (
                            <List>
                                {result.user_selected_answers.map((answer) => (
                                    <ListItem key={answer.id}>
                                        <Sheet color={getStatusColor(result.status)} variant='soft' sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 'lg', p: 1 }}>
                                            <Typography
                                                startDecorator={getStatusIcon(result.status)}
                                                level="body1"
                                            >
                                                {t(answer.text, answer.text_ru)}
                                            </Typography>
                                        </Sheet>
                                        {/* {answer.description && (
                                            <SafeHTML 
                                                html={answer.description}
                                                sx={{ 
                                                    ml: 2,
                                                    fontSize: 'sm',
                                                    color: 'text.secondary'
                                                }}
                                            />
                                        )} */}
                                    </ListItem>
                                ))}
                                {result.user_selected_answers.length === 0 && (
                                    <ListItem>
                                        <Typography level="body1" color="neutral">
                                            Ответ не выбран
                                        </Typography>
                                    </ListItem>
                                )}
                            </List>
                        )}
                    </Box>

                    {/* Правильные ответы */}
                    <Box>
                        <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                            Правильные ответы:
                        </Typography>
                        <List>
                            {result.correct_answers.map((answer) => (
                                <ListItem key={answer.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <CheckCircle size={18} weight="fill" color="var(--joy-palette-success-500)" style={{ marginTop: 4 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography level="body1">
                                                {t(answer.text, answer.text_ru)}
                                            </Typography>
                                            {answer.description && (
                                                <SafeHTML 
                                                    html={answer.description}
                                                    sx={{ 
                                                        mt: 0.5,
                                                        fontSize: 'sm',
                                                        color: 'text.secondary',
                                                        '& p': { mb: 0.5 }
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    {/* Объяснение (только для неправильных ответов) */}
                    {result.status === 'incorrect' && result.question_explanation && (
                        <Alert color="primary" variant="soft">
                            <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                Объяснение:
                            </Typography>
                            <SafeHTML
                                html={t(result.question_explanation, result.question_explanation_ru)}
                                sx={{ 
                                    color: 'text.secondary',
                                    '& p': { mb: 0.5 }
                                }}
                            />
                        </Alert>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}